import { useAuth } from "@/auth";
import { Button } from "@/components/generic/Button";
import { TextField } from "@/components/generic/TextField";
import { MaybePromise } from "@/types/maybe-promise";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../../basic/SegmentHeader";

export const completionEmailFormSchema = z.object({
  completionEmail: z.email("Ugyldig e-mail adresse"),
});

export type CompletionEmailFormValues = z.infer<
  typeof completionEmailFormSchema
>;

export function CompletionEmailForm({
  defaultValues,
  values,
  updateValues,
  showSubmitButton = false,
  setSubmitHandler,
  autoSubmit = false,
}: {
  defaultValues: CompletionEmailFormValues;
  values?: CompletionEmailFormValues;
  updateValues: (values: CompletionEmailFormValues) => MaybePromise<void>;
  showSubmitButton?: boolean;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  autoSubmit?: boolean;
}) {
  const {
    reset,
    setValue,
    control,
    handleSubmit,
    formState: {
      isDirty,
      isValid,
      isSubmitted,
      isSubmitting,
      defaultValues: formCurrentDefaultValues,
    },
  } = useForm({
    resolver: zodResolver(completionEmailFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues === formCurrentDefaultValues) return;
    reset(defaultValues, { keepValues: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    if (!values) return;
    setValue("completionEmail", values.completionEmail);
  }, [values, setValue]);

  const submit = useMemo(
    () => handleSubmit((values) => updateValues(values)),
    [handleSubmit, updateValues],
  );

  const canSubmit = useMemo(
    () => !isSubmitting && (isValid || !isSubmitted),
    [isSubmitted, isSubmitting, isValid],
  );

  useEffect(
    () =>
      setSubmitHandler?.({
        submit,
        isDirty,
        isPending: isSubmitting,
        isDisabled: !canSubmit,
      }),
    [canSubmit, isDirty, isSubmitting, setSubmitHandler, submit],
  );

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
  }, [autoSubmit, submit]);

  return (
    <form className="flex flex-col items-center gap-y-10" onSubmit={submit}>
      <SegmentHeader
        className="max-w-lg"
        title="Email til modtagelse af leads"
        description="Indtast den email-adresse, hvor du ønsker at modtage Excel-filen med leads."
      />

      <TextField
        className="w-full max-w-xs"
        control={control}
        name="completionEmail"
        type="email"
        placeholder="Indtast e-mail"
        aria-label="Email til modtagelse af leads"
        autoFocus
      />

      {showSubmitButton && (
        <Button
          className="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          type="submit"
          isDisabled={!canSubmit}
        >
          Gem
        </Button>
      )}
    </form>
  );
}
